import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type { WalletDto } from "@/features/wallet/api/dto";
import type { WalletStatus } from "@/features/shared/api/enums";

/**
 * Force le statut d'un portefeuille
 * (`POST /Wallets/{id}/status?status=...` — statut en query param, enum
 * majuscules, guide §6.3). Cibles admises : ACTIVE, SUSPENDED, CLOSED. Une
 * suspension bloque immédiatement toute nouvelle transaction. Back-office
 * (flashpay-superadmin, flashpay-admin).
 */
export const useSetWalletStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "wallets", "set-status"],
    mutationFn: ({
      walletId,
      status,
    }: {
      walletId: string;
      status: WalletStatus;
    }) =>
      mutateData({
        url: `${walletEndpoints.wallets.status(walletId)}?status=${status}`,
        method: "POST",
      }) as Promise<WalletDto>,
    onSuccess: (_data, { walletId }) => {
      return invalidateAfterMutation(
        queryClient,
        walletWriteCacheKeys(walletId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSetWalletStatus failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
